import React, { useCallback, useMemo, useState } from 'react';

import { getCurrencyConfig } from '../../config/config';
import { useChanceToMatch } from '../../hooks/useChanceToMatch';
import { usePulseAnimation } from '../../hooks/usePulseAnimation';
import { Currency } from '../../types/currency';
import { LoanType } from '../../types/loanType';
import { checkInstantAvailability } from '../../utils/checkInstantAvailability';
import { checkOtcAvailability } from '../../utils/checkOtcAvailability';
import { CustomLoanCard } from './CustomLoanCard';
import { InstantLoanCard } from './InstantLoanCard';
import styles from './LoanTypeSection.module.less';
import { OtcCard } from './OtcCard';

const PULSE_ANIMATION_DURATION = 400;

interface LoanTypeSectionProps {
  currency: Currency;
  amount: string;
  period: number;
  loanType: LoanType;
  customInterestRate: string;
  onLoanTypeChange: (type: LoanType) => void;
  onCustomInterestRateChange: (rate: string | number) => void;
}

export const LoanTypeSection: React.FC<LoanTypeSectionProps> = ({
  currency,
  amount,
  period,
  loanType,
  customInterestRate,
  onLoanTypeChange,
  onCustomInterestRateChange,
}) => {
  const { computeChanceToMatch } = useChanceToMatch();

  const [cardTrigger, setCardTrigger] = useState(0);
  const [pulsingCard, setPulsingCard] = useState<LoanType | null>(null);
  const pulsingCardAnimation = usePulseAnimation(
    cardTrigger,
    PULSE_ANIMATION_DURATION
  );

  const currencyConfig = getCurrencyConfig(currency);

  // Get instant rate from invest tickets
  const instantRate = useMemo(() => {
    if (!currencyConfig) return undefined;
    const ticket = currencyConfig.investTickets.find(t => t.period === period);
    return ticket?.interestRate;
  }, [currencyConfig, period]);

  // Check instant availability
  const instantAvailability = useMemo(() => {
    if (!currencyConfig) {
      return { available: false, reason: 'no_currency_instant_rate' as const };
    }

    const ticket = currencyConfig.investTickets.find(t => t.period === period);
    const availablePeriods = currencyConfig.investTickets.map(t => t.period);

    return checkInstantAvailability({
      instantRate: ticket?.interestRate ?? null,
      amount,
      maxInstantTransactionLimit: currencyConfig.maxInstantTransactionLimit,
      availableTicketAmount: ticket?.amount ?? '0',
      availableInstantPeriods: availablePeriods,
    });
  }, [currencyConfig, amount, period]);

  // Check OTC availability
  const otcAvailability = useMemo(() => {
    if (!currencyConfig) {
      return { available: false };
    }

    return checkOtcAvailability({
      amount,
      minOtcTransactionLimit: currencyConfig.minOtcTransactionLimit,
    });
  }, [currencyConfig, amount]);

  // Compute chance to match
  const chanceToMatch = useMemo(() => {
    const numericAmount = parseFloat(amount) || 0;
    const numericRate = parseFloat(customInterestRate) || 0;

    return computeChanceToMatch({
      amount: numericAmount,
      interestRate: numericRate,
      period,
      currency,
    });
  }, [amount, customInterestRate, period, currency, computeChanceToMatch]);

  const handleCardClick = useCallback((cardType: LoanType) => {
    setPulsingCard(cardType);
    setCardTrigger(prev => prev + 1);
  }, []);

  return (
    <>
      <div className={styles.loanTypeCardsContainer}>
        <div className={styles.loanTypeStepContent}>
          <InstantLoanCard
            selected={loanType === LoanType.Instant}
            instantRate={instantRate}
            instantAvailability={instantAvailability}
            currency={currency}
            onSelect={() => {
              const newType = loanType === LoanType.Instant ? LoanType.None : LoanType.Instant;
              handleCardClick(LoanType.Instant);
              onLoanTypeChange(newType);
            }}
            isPulsing={pulsingCard === LoanType.Instant && pulsingCardAnimation}
          />

          <CustomLoanCard
            selected={loanType === LoanType.Custom}
            customInterestRate={customInterestRate}
            onCustomInterestRateChange={onCustomInterestRateChange}
            onSelect={() => {
              const newType = loanType === LoanType.Custom ? LoanType.None : LoanType.Custom;
              handleCardClick(LoanType.Custom);
              onLoanTypeChange(newType);
            }}
            isPulsing={pulsingCard === LoanType.Custom && pulsingCardAnimation}
            chanceToMatch={chanceToMatch}
            minRate={parseFloat(currencyConfig?.minInterestRate ?? '0')}
            maxRate={parseFloat(currencyConfig?.maxInterestRate ?? '16')}
          />

          <OtcCard
            selected={loanType === LoanType.Otc}
            otcAvailability={otcAvailability}
            currency={currency}
            onSelect={() => {
              const newType = loanType === LoanType.Otc ? LoanType.None : LoanType.Otc;
              handleCardClick(LoanType.Otc);
              onLoanTypeChange(newType);
            }}
            isPulsing={pulsingCard === LoanType.Otc && pulsingCardAnimation}
          />
        </div>
      </div>
    </>
  );
};
